import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FolderOpen, Images, Trash2, Play, X, Settings } from 'lucide-react';
import clsx from 'clsx';

import { useApi } from '../api-context';
import { Spinner } from '../components/Primitives';
import SettingsPanel from '../components/SettingsPanel';
import ConfirmDialog from '../components/ConfirmDialog';
import type { MediaItem, Project, ScanProgress } from '../../types';
import { useThumbnail } from '../hooks/useImageUrl';

type Sort = 'recent' | 'name' | 'unfinished';
type Filter = 'all' | 'in-progress' | 'finished';

const COVER = 4;

/**
 * The library — every shoot in one place.
 *
 * Separate from the home page because they serve different people: the home
 * page has to convince a stranger, this has to get someone who already uses
 * m3cull back into their work in one click.
 */
export default function Shoots() {
  const api = useApi();
  const navigate = useNavigate();

  const [projects, setProjects] = useState<Project[]>([]);
  const [covers, setCovers] = useState<Record<number, MediaItem[]>>({});
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<Sort>('recent');
  const [filter, setFilter] = useState<Filter>('all');
  const [scan, setScan] = useState<ScanProgress | null>(null);
  const [busy, setBusy] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Project | null>(null);

  const refresh = useCallback(() => {
    const list = api.listProjects();
    setProjects(list);
    setCovers(
      Object.fromEntries(
        list.map((p) => [
          p.id,
          api.listMedia(p.id, { limit: COVER, offset: 0, filter: 'all' }).items,
        ]),
      ),
    );
  }, [api]);

  useEffect(refresh, [refresh]);

  const startImport = async (
    run: () => Promise<{ project: Project; added: number } | null>,
    nothingFound: string,
  ) => {
    setError(null);
    setBusy(true);
    try {
      const result = await run();
      if (!result) return;
      if (result.added === 0) {
        setError(nothingFound);
        api.deleteProject(result.project.id);
        refresh();
        return;
      }
      // The shoot page, not the culling screen. It is the only screen that
      // offers the review, reports its progress and shows what it produced —
      // and with a review on the table, dropping someone straight into manual
      // culling makes the choice for them.
      navigate(`/p/${result.project.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
      setScan(null);
    }
  };

  const remove = (project: Project) => {
    api.deleteProject(project.id);
    setPendingDelete(null);
    refresh();
  };

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    let list = projects.filter((p) => !needle || p.name.toLowerCase().includes(needle));

    if (filter === 'in-progress') list = list.filter((p) => p.reviewedCount < p.itemCount);
    if (filter === 'finished') {
      list = list.filter((p) => p.itemCount > 0 && p.reviewedCount >= p.itemCount);
    }

    return [...list].sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name);
      if (sort === 'unfinished') {
        return b.itemCount - b.reviewedCount - (a.itemCount - a.reviewedCount);
      }
      return (b.lastOpenedAt ?? b.createdAt) - (a.lastOpenedAt ?? a.createdAt);
    });
  }, [projects, query, sort, filter]);

  const empty = projects.length === 0;

  return (
    <div className="flex min-h-full flex-col">
      <header className="sticky top-0 z-30 border-b border-ink-850 bg-ink-950/85 backdrop-blur-xl">
        <div className="mx-auto flex h-14 w-full max-w-content items-center gap-4 px-5 sm:px-8">
          <button onClick={() => navigate('/')} aria-label="m3cull home">
            <img
              src={`${import.meta.env.BASE_URL}brand/lockup.png`}
              alt="m3cull"
              width={394}
              height={96}
              className="h-6 w-auto"
            />
          </button>
          <span className="ml-auto text-caption text-ink-500">
            {projects.length} {projects.length === 1 ? 'shoot' : 'shoots'}
          </span>
          <button
            className="rounded-lg p-1.5 text-ink-500 transition hover:bg-ink-850 hover:text-ink-100"
            onClick={() => setShowSettings(true)}
            aria-label="Settings"
            title="Settings"
          >
            <Settings size={16} />
          </button>
        </div>
      </header>

      <main id="main" className="mx-auto w-full max-w-content flex-1 px-5 pb-24 pt-10 sm:px-8 sm:pt-14">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="label">Library</p>
            <h1 className="mt-2.5 text-section font-semibold tracking-tight text-ink-50">
              Your shoots
            </h1>
          </div>

          <div className="flex flex-wrap gap-2.5">
            <button
              className="btn-primary px-5 py-2.5 text-body"
              disabled={busy}
              onClick={() =>
                startImport(
                  () => api.importFolder(setScan),
                  'That folder has no photos or videos in it.',
                )
              }
            >
              {busy && !scan ? <Spinner className="h-4 w-4" /> : <FolderOpen size={15} />}
              New shoot
            </button>
            <button
              className="btn-outline px-4 py-2.5 text-body"
              disabled={busy}
              onClick={() =>
                startImport(
                  () => api.importFiles(setScan),
                  'None of those files are photos or videos m3cull can read.',
                )
              }
            >
              <Images size={15} />
              Choose photos
            </button>
          </div>
        </div>

        {scan && (
          <div className="card mt-6 flex items-center gap-3 p-4">
            <Spinner className="h-4 w-4 shrink-0 text-ink-400" />
            <p className="truncate text-caption text-ink-100">
              {scan.sourceName}: {scan.scanned} found
            </p>
          </div>
        )}

        {error && (
          <p className="mt-6 rounded-lg border border-reject-line bg-reject-soft px-4 py-3 text-caption text-reject">
            {error}
          </p>
        )}

        {!empty && (
          <div className="mt-9 flex flex-col gap-3 border-b border-ink-850 pb-4 sm:flex-row sm:items-center">
            <label className="relative flex-1">
              <Search
                size={15}
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-500"
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search shoots"
                className="w-full rounded-lg border border-ink-800 bg-ink-900 py-2.5 pl-10 pr-9 text-body text-ink-100 placeholder:text-ink-600 focus-visible:border-ink-600"
              />
              {query && (
                <button
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-100"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </label>

            <Segmented
              value={filter}
              onChange={setFilter}
              options={[
                { value: 'all', label: 'All' },
                { value: 'in-progress', label: 'In progress' },
                { value: 'finished', label: 'Finished' },
              ]}
            />
            <Segmented
              value={sort}
              onChange={setSort}
              options={[
                { value: 'recent', label: 'Recent' },
                { value: 'name', label: 'Name' },
                { value: 'unfinished', label: 'Most left' },
              ]}
            />
          </div>
        )}

        {empty ? (
          <div className="mt-16 flex flex-col items-center gap-4 py-16 text-center">
            <p className="text-head font-medium text-ink-100">No shoots yet</p>
            <p className="max-w-sm text-caption text-ink-500">
              Open a folder of photographs and m3cull will keep it here, with your decisions, until
              you come back.
            </p>
            {/* The buttons at the top of the page are a long way from the middle
                of an empty one. Somewhere to go beats somewhere to read. */}
            <button
              className="btn-primary mt-2 px-5 py-2.5 text-body"
              disabled={busy}
              onClick={() =>
                startImport(
                  () => api.importFolder(setScan),
                  'That folder has no photos or videos in it.',
                )
              }
            >
              {busy && !scan ? <Spinner className="h-4 w-4" /> : <FolderOpen size={15} />}
              Open a folder
            </button>
          </div>
        ) : shown.length === 0 ? (
          <p className="mt-16 text-center text-caption text-ink-500">
            Nothing matches “{query}”.
          </p>
        ) : (
          <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((project) => (
              <ShootCard
                key={project.id}
                project={project}
                cover={covers[project.id] ?? []}
                onOpen={() => navigate(`/p/${project.id}`)}
                onReview={() => navigate(`/p/${project.id}/review`)}
                onDelete={() => setPendingDelete(project)}
              />
            ))}
          </ul>
        )}
      </main>

      {showSettings && <SettingsPanel onClose={() => setShowSettings(false)} />}

      {pendingDelete && (
        <ConfirmDialog
          title={`Delete “${pendingDelete.name}”?`}
          body="Only m3cull's record of the shoot goes. Your photographs stay exactly where they are, untouched."
          confirmLabel="Delete the shoot"
          onConfirm={() => remove(pendingDelete)}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}

function ShootCard({
  project,
  cover,
  onOpen,
  onReview,
  onDelete,
}: {
  project: Project;
  cover: MediaItem[];
  onOpen: () => void;
  onReview: () => void;
  onDelete: () => void;
}) {
  const left = project.itemCount - project.reviewedCount;
  const done = project.itemCount > 0 ? project.reviewedCount / project.itemCount : 0;

  return (
    <li className="group relative flex flex-col overflow-hidden rounded-xl border border-ink-800 bg-ink-900 transition hover:border-ink-700">
      <button onClick={onOpen} className="block text-left" aria-label={`Open ${project.name}`}>
        <span className="absolute inset-0 z-0" aria-hidden="true" />
        <div className="grid grid-cols-4 gap-px bg-ink-850">
          {Array.from({ length: COVER }, (_, i) => (
            <Tile key={i} item={cover[i]} />
          ))}
        </div>
      </button>

      <div className="relative z-10 flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <p className="min-w-0 flex-1 truncate text-body font-medium text-ink-50">
            {project.name}
          </p>
          <button
            className="pointer-events-auto shrink-0 text-ink-600 opacity-0 transition hover:text-reject group-hover:opacity-100 focus-visible:opacity-100"
            onClick={onDelete}
            aria-label={`Delete ${project.name}`}
          >
            <Trash2 size={14} />
          </button>
        </div>

        <p className="tnum text-tiny text-ink-500">
          {project.itemCount} photos
          <span className="px-1.5 text-ink-700" aria-hidden="true">·</span>
          <span className={project.keptCount > 0 ? 'text-keep' : undefined}>
            {project.keptCount} kept
          </span>
          <span className="px-1.5 text-ink-700" aria-hidden="true">·</span>
          {left === 0 ? 'done' : `${left} left`}
        </p>

        <div className="mt-auto flex items-center gap-3 pt-1">
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-ink-850">
            <div
              className={clsx('h-full rounded-full', left === 0 ? 'bg-keep' : 'bg-ink-300')}
              style={{ width: `${done * 100}%` }}
            />
          </div>
          <button
            className="pointer-events-auto relative z-10 flex shrink-0 items-center gap-1.5 text-tiny text-ink-400 transition hover:text-ink-50"
            onClick={onReview}
          >
            <Play size={12} />
            {left > 0 ? 'Continue' : 'Review'}
          </button>
        </div>
      </div>
    </li>
  );
}

function Tile({ item }: { item?: MediaItem }) {
  const url = useThumbnail(item ?? null);
  return (
    <div className="aspect-square overflow-hidden bg-ink-950">
      {url ? (
        <img src={url} alt="" loading="lazy" className="h-full w-full object-cover" />
      ) : (
        <div className="h-full w-full bg-ink-850" />
      )}
    </div>
  );
}

function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div className="flex shrink-0 rounded-lg border border-ink-800 p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={clsx(
            'rounded-md px-3 py-1.5 text-caption transition',
            option.value === value ? 'bg-ink-800 text-ink-50' : 'text-ink-400 hover:text-ink-100',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
